import './PageEditor.scss';
import * as React from 'react';
import { Link, Route, RouteComponentProps, Switch } from 'react-router-dom';
import { INewPage, IPage, PageStatus } from 'src/Entities/Pages';
import Breadcrumb from 'src/components/Breadcrumb';
import BreadcrumbItem from 'src/components/Breadcrumb/BreadcrumbItem';
import Wrapper from 'src/components/Wrapper';
import PageListEditor from './components/PageListEditor';
import { ToastContainer, addToast, clearToasts } from 'src/components/Toast';
import PageForm from './components/PageForm';
import InitialMessage from './components/InitialMessage';
import { usePages } from 'src/context/pages';
import { usePageModals } from 'src/shared/Modal';
import DeleteModal from 'src/components/DeleteModal';

interface IPageEditorState {
  pages: IPage[];
}

interface IPageEditorParams {
  id?: string | 'opret';
}

const PageEditor = ({ match, history }: RouteComponentProps<IPageEditorParams>) => {
  const {
    fetchList,
    createPage,
    updatePage,
    deletePage,
    reorderPages,
    list
  } = usePages();
  const modals = usePageModals();

  const [state, setState] = React.useState<IPageEditorState>({
    pages: []
  });
  const activePageId: number | null = React.useMemo(
    () => match.params.id ? parseInt(match.params.id, 10) : null,
    [match]
  );
  const activePage = React.useMemo<IPage | null>((): IPage | null => {
    const foundPage = list.find((page: IPage) => page.portalPageId === activePageId);
    return foundPage || null;
  }, [list, activePageId]);
  const activePageRef = React.useRef<IPage | null>(activePage);

  React.useEffect(() => {
    const previousActivePage = activePageRef.current;
    activePageRef.current = activePage;

    if (previousActivePage && previousActivePage.status === PageStatus.DRAFTED) {
      if (activePage && activePage.portalPageId === previousActivePage.portalPageId) {
        return;
      }

      handleDelete(previousActivePage, false);
    }
  }, [activePage]);

  const fetch = React.useCallback(() => {
    fetchList();
  }, [state]);

  const handleOrderChange = async (orderedPages: IPage[]) => {
    await reorderPages(orderedPages);
  };

  const handleCreateSubPage = async (newPage: INewPage) => {
    const page = await createPage(newPage);
    history.replace(`/cms/sider/${page.portalPageId}`);
  };

  const handleDelete = async (page: IPage, redirect: boolean = true) => {
    await deletePage(page.portalPageId);
    if (redirect) {
      history.replace('/cms/sider');
      addToast({
        type: 'info',
        message: 'Siden blev slettet'
      });
    }
  };

  const handleChangeStatus = async (status: PageStatus, pageToUpdate: IPage) => {
    const updatedPage = { ...pageToUpdate, status };
    await updatePage(updatedPage);
    clearToasts();
    addToast({
      type: 'info',
      message: status === PageStatus.PUBLISHED
        ? 'Siden er nu synlig'
        : 'Siden er nu skjult',
    });
  };

  React.useEffect(() => {
    fetch();
  }, []);

  return (
    <div className="has-background-gray-body pb-14">
      <Wrapper>
        <Breadcrumb className="pt3">
          <BreadcrumbItem>
            <Link to="/">Forside</Link>
          </BreadcrumbItem>
          <BreadcrumbItem active={true}>
            <Link to="">Sider</Link>
          </BreadcrumbItem>
        </Breadcrumb>
        <h1 className="uppercase font-light text-lg my-5">Rediger sider</h1>
        <div className="flex">
          <div className="has-background-light-3 py-6 pl-6 w-96 flex-none relative PageListContainer">
            <PageListEditor
              onOrderChange={handleOrderChange}
              onSelectPage={async (page) => {
                history.push(`/cms/sider/${page.portalPageId}`)
              }}
              onCreateSubPage={handleCreateSubPage}
              pages={list}
              activePageId={activePageId}
            />
          </div>
          <div className="has-background-white relative flex-1 flex">
            <Switch>
              <Route path="/cms/sider" exact={true} component={InitialMessage} />
              <Route path="/cms/sider/:id">
                <div className="p-14 flex-1">
                  {activePage !== null && (
                    <PageForm
                      page={activePage}
                      history={history}
                      onCancel={() => history.replace('/cms/sider')}
                      onDelete={(page) => {
                        modals.open((modal) => (
                          <DeleteModal
                            cancelButtonText="Nej - behold siden"
                            deleteButtonText="Ja - slet siden"
                            onDelete={() => {
                              return handleDelete(page, true);
                            }}
                            onCancel={modal.close}
                            onClose={modal.close}
                            title={<>
                              {'Du har valgt at slette '}
                              {page.title
                                ? <span className="font-bold">{page.title}</span>
                                : 'denne side'}
                              {'.'}
                              <br />
                              {'Er det korrekt?'}
                            </>}
                          />
                        ))
                      }}
                      onChangeStatus={handleChangeStatus}
                      onSubmit={async (updatedPage) => {
                        await updatePage(updatedPage);
                        clearToasts();
                        addToast({ type: 'info', message: 'Siden blev gemt' });
                      }}
                    />
                  )}
                </div>
              </Route>
            </Switch>

            <ToastContainer />
          </div>
        </div>
      </Wrapper>
    </div>
  );
};

export default PageEditor;



// WEBPACK FOOTER //
// ./src/Pages/PageEditor/index.tsx